/**
 * AppContext - Centralized state management for the main application
 * 
 * This context manages the core application state including:
 * - Report text and related operations
 * - Loading states for different operations
 * - UI preferences (theme, font size)
 * - Modal states
 * 
 * Benefits:
 * - Eliminates prop drilling
 * - Centralizes state logic
 * - Provides better separation of concerns
 * - Makes testing easier
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial state
const initialState = {
  // Core content
  reportText: '',
  suggestions: '',
  supervisorReview: '',
  lawyerReview: '',
  ghostText: '',
  
  // Loading states
  loading: false,
  supervisorLoading: false,
  lawyerLoading: false,
  isDrafting: false,
  contactFormLoading: false,
  
  // UI preferences
  theme: 'light',
  fontSize: 'normal',
  editorWidth: 65,
  
  // Modal states
  modals: {
    supervisor: false,
    lawyer: false,
    contact: false,
    quickDraft: false,
    rewrite: false
  },
  
  // Contact form
  contactFormStatus: '',
  
  // Rewrite feature
  selectedText: '',
  showRewriteButton: false,
  buttonPosition: { x: 0, y: 0 },
  
  // Voice input
  isListening: false
};

// Action types
const ACTION_TYPES = {
  // Content actions
  SET_REPORT_TEXT: 'SET_REPORT_TEXT',
  SET_SUGGESTIONS: 'SET_SUGGESTIONS',
  SET_SUPERVISOR_REVIEW: 'SET_SUPERVISOR_REVIEW',
  SET_LAWYER_REVIEW: 'SET_LAWYER_REVIEW',
  SET_GHOST_TEXT: 'SET_GHOST_TEXT',
  
  // Loading actions
  SET_LOADING: 'SET_LOADING',
  SET_SUPERVISOR_LOADING: 'SET_SUPERVISOR_LOADING',
  SET_LAWYER_LOADING: 'SET_LAWYER_LOADING',
  SET_DRAFTING: 'SET_DRAFTING',
  SET_CONTACT_FORM_LOADING: 'SET_CONTACT_FORM_LOADING',
  
  // UI actions
  SET_THEME: 'SET_THEME',
  SET_FONT_SIZE: 'SET_FONT_SIZE',
  TOGGLE_THEME: 'TOGGLE_THEME',
  
  // Modal actions
  SET_MODAL_STATE: 'SET_MODAL_STATE',
  CLOSE_ALL_MODALS: 'CLOSE_ALL_MODALS',
  
  // Contact form actions
  SET_CONTACT_FORM_STATUS: 'SET_CONTACT_FORM_STATUS',
  
  // Rewrite actions
  SET_SELECTED_TEXT: 'SET_SELECTED_TEXT',
  SET_REWRITE_BUTTON: 'SET_REWRITE_BUTTON',
  SET_BUTTON_POSITION: 'SET_BUTTON_POSITION',
  
  // Voice actions
  SET_LISTENING: 'SET_LISTENING'
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_REPORT_TEXT:
      return { 
        ...state, 
        reportText: typeof action.payload === 'function' 
          ? action.payload(state.reportText) 
          : action.payload 
      };
      
    case ACTION_TYPES.SET_SUGGESTIONS:
      return { ...state, suggestions: action.payload };
      
    case ACTION_TYPES.SET_SUPERVISOR_REVIEW:
      return { ...state, supervisorReview: action.payload };
      
    case ACTION_TYPES.SET_LAWYER_REVIEW:
      return { ...state, lawyerReview: action.payload };
      
    case ACTION_TYPES.SET_GHOST_TEXT:
      return { ...state, ghostText: action.payload };
      
    case ACTION_TYPES.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ACTION_TYPES.SET_SUPERVISOR_LOADING:
      return { ...state, supervisorLoading: action.payload };
      
    case ACTION_TYPES.SET_LAWYER_LOADING:
      return { ...state, lawyerLoading: action.payload };
      
    case ACTION_TYPES.SET_DRAFTING:
      return { ...state, isDrafting: action.payload };
      
    case ACTION_TYPES.SET_CONTACT_FORM_LOADING:
      return { ...state, contactFormLoading: action.payload };
      
    case ACTION_TYPES.SET_THEME:
      return { ...state, theme: action.payload };
      
    case ACTION_TYPES.TOGGLE_THEME:
      return { ...state, theme: state.theme === 'light' ? 'dark' : 'light' };
      
    case ACTION_TYPES.SET_FONT_SIZE:
      return { ...state, fontSize: action.payload };
      
    case ACTION_TYPES.SET_MODAL_STATE:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload.modal]: action.payload.isOpen
        }
      };
      
    case ACTION_TYPES.CLOSE_ALL_MODALS:
      return {
        ...state,
        modals: Object.keys(state.modals).reduce((acc, key) => {
          acc[key] = false;
          return acc;
        }, {})
      };
      
    case ACTION_TYPES.SET_CONTACT_FORM_STATUS:
      return { ...state, contactFormStatus: action.payload };
      
    case ACTION_TYPES.SET_SELECTED_TEXT:
      return { ...state, selectedText: action.payload };
      
    case ACTION_TYPES.SET_REWRITE_BUTTON:
      return { ...state, showRewriteButton: action.payload };
      
    case ACTION_TYPES.SET_BUTTON_POSITION:
      return { ...state, buttonPosition: action.payload };
      
    case ACTION_TYPES.SET_LISTENING:
      return { ...state, isListening: action.payload };
      
    default:
      return state;
  }
}

// Create context
const AppContext = createContext(null);

/**
 * AppProvider - Provides application state and actions to child components
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Action creators - wrapped in useCallback for performance
  const actions = {
    // Content actions
    setReportText: useCallback((text) => {
      dispatch({ type: ACTION_TYPES.SET_REPORT_TEXT, payload: text });
    }, []),
    
    setSuggestions: useCallback((suggestions) => {
      dispatch({ type: ACTION_TYPES.SET_SUGGESTIONS, payload: suggestions });
    }, []),
    
    setSupervisorReview: useCallback((review) => {
      dispatch({ type: ACTION_TYPES.SET_SUPERVISOR_REVIEW, payload: review });
    }, []),
    
    setLawyerReview: useCallback((review) => {
      dispatch({ type: ACTION_TYPES.SET_LAWYER_REVIEW, payload: review });
    }, []),
    
    setGhostText: useCallback((text) => {
      dispatch({ type: ACTION_TYPES.SET_GHOST_TEXT, payload: text });
    }, []),
    
    // Loading actions
    setLoading: useCallback((isLoading) => {
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: isLoading });
    }, []),
    
    setSupervisorLoading: useCallback((isLoading) => {
      dispatch({ type: ACTION_TYPES.SET_SUPERVISOR_LOADING, payload: isLoading });
    }, []),
    
    setLawyerLoading: useCallback((isLoading) => {
      dispatch({ type: ACTION_TYPES.SET_LAWYER_LOADING, payload: isLoading });
    }, []),
    
    setDrafting: useCallback((isDrafting) => {
      dispatch({ type: ACTION_TYPES.SET_DRAFTING, payload: isDrafting });
    }, []),
    
    setContactFormLoading: useCallback((isLoading) => {
      dispatch({ type: ACTION_TYPES.SET_CONTACT_FORM_LOADING, payload: isLoading });
    }, []),
    
    // UI actions
    setTheme: useCallback((theme) => {
      dispatch({ type: ACTION_TYPES.SET_THEME, payload: theme });
    }, []),
    
    toggleTheme: useCallback(() => {
      dispatch({ type: ACTION_TYPES.TOGGLE_THEME });
    }, []),
    
    setFontSize: useCallback((size) => {
      dispatch({ type: ACTION_TYPES.SET_FONT_SIZE, payload: size });
    }, []),
    
    // Modal actions
    setModalState: useCallback((modal, isOpen) => {
      dispatch({ type: ACTION_TYPES.SET_MODAL_STATE, payload: { modal, isOpen } });
    }, []),
    
    closeAllModals: useCallback(() => {
      dispatch({ type: ACTION_TYPES.CLOSE_ALL_MODALS });
    }, []),
    
    // Contact form actions
    setContactFormStatus: useCallback((status) => {
      dispatch({ type: ACTION_TYPES.SET_CONTACT_FORM_STATUS, payload: status });
    }, []),
    
    // Rewrite actions
    setSelectedText: useCallback((text) => {
      dispatch({ type: ACTION_TYPES.SET_SELECTED_TEXT, payload: text });
    }, []),
    
    setRewriteButton: useCallback((show) => {
      dispatch({ type: ACTION_TYPES.SET_REWRITE_BUTTON, payload: show });
    }, []),
    
    setButtonPosition: useCallback((position) => {
      dispatch({ type: ACTION_TYPES.SET_BUTTON_POSITION, payload: position });
    }, []),
    
    // Voice actions
    setListening: useCallback((isListening) => {
      dispatch({ type: ACTION_TYPES.SET_LISTENING, payload: isListening });
    }, [])
  };

  const value = {
    state,
    actions,
    // Helper getters for commonly accessed computed values
    get isRewriteModalOpen() {
      return state.modals.rewrite;
    },
    get isSupervisorModalOpen() {
      return state.modals.supervisor;
    },
    get isLawyerModalOpen() {
      return state.modals.lawyer;
    },
    get isContactModalOpen() {
      return state.modals.contact;
    },
    get isQuickDraftModalOpen() {
      return state.modals.quickDraft;
    }
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

/**
 * Custom hook to use the App context
 * 
 * @throws {Error} When used outside of AppProvider
 * @returns {Object} The app context value
 */
export function useAppContext() {
  const context = useContext(AppContext);
  
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  
  return context;
}

export { ACTION_TYPES };